/* Native community-signup form on /pages/communities.
 *
 * Posts the SAME multipart contract the self-serve SPA uses (a `payload` JSON
 * part, no image) to the worker at <worker_base>/api/submit, which creates the
 * discount + community metaobject and — because we send `marketing_consent`
 * (the form shows the consent line) — a Klaviyo profile on the "Communities
 * Inquiries" list. The worker re-validates everything and is authoritative; the
 * client checks below are UX only.
 *
 * The theme has no build step, so the tier/market/validation rules from the
 * worker's shared/community.ts are mirrored here in plain JS. Keep them in sync.
 */
(function () {
  var form = document.querySelector("[data-community-signup]");
  if (!form) return;

  var WORKER_BASE = (form.getAttribute("data-worker-base") || "https://newcommunity.tincan.kids").replace(/\/+$/, "");
  var TURNSTILE_SITEKEY = form.getAttribute("data-turnstile-sitekey") || "";
  // The active store market (Liquid set it from cart.currency). The market
  // picker is pre-selected to this server-side, and the tier ladder under the
  // devices question is server-rendered for it — so this is the default.
  var ACTIVE_MARKET = form.getAttribute("data-active-market") || "US";

  var MIN_DEVICES = 25;
  var MAX_WINDOW_DAYS = 30;
  var EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
  var ORG_TYPES = ["Public School", "Private School", "PTA/Parent Org", "Non-Profit", "Other"];
  var MARKET_HANDLES = ["US", "CA", "AU", "NZ", "GB"];

  // Offline fallback (mirrors the worker's MARKET_FALLBACKS — display only).
  var FALLBACK_MARKETS = [
    mkMarket("US", "United States", "USD", 10000),
    mkMarket("CA", "Canada", "CAD", 13500),
    mkMarket("AU", "Australia", "AUD", 18000),
    mkMarket("NZ", "New Zealand", "NZD", 19000),
    mkMarket("GB", "United Kingdom", "GBP", 10000)
  ];
  function mkMarket(handle, label, currency, priceCents) {
    var percents = { tier_0: 20, tier_1: 25, tier_2: 30, tier_3: 35 };
    var off = {};
    ["tier_0", "tier_1", "tier_2", "tier_3"].forEach(function (t) {
      off[t] = Math.floor((priceCents * percents[t]) / 100 / 100) * 100;
    });
    return {
      market_handle: handle,
      label: label,
      currency_code: currency,
      price_cents: priceCents,
      percents: percents,
      tier_off_cents: off
    };
  }

  var markets = FALLBACK_MARKETS.slice();
  var marketTouched = false;

  // ── Elements ────────────────────────────────────────────────────────────
  var els = {
    display_name: form.querySelector('[name="display_name"]'),
    organizer_name: form.querySelector('[name="organizer_name"]'),
    organizer_email: form.querySelector('[name="organizer_email"]'),
    organizer_phone: form.querySelector('[name="organizer_phone"]'),
    org_type: form.querySelector('[name="org_type"]'),
    market_handle: form.querySelector("[data-market-select]"),
    community_city_state: form.querySelector('[name="community_city_state"]'),
    target_devices: form.querySelector('[name="target_devices"]'),
    window_opens_at: form.querySelector('[name="window_opens_at"]'),
    window_closes_at: form.querySelector('[name="window_closes_at"]'),
    community_blurb: form.querySelector('[name="community_blurb"]')
  };
  var marketHint = form.querySelector("[data-market-hint]");
  var devicesHint = form.querySelector("[data-devices-hint]");
  var submitBtn = form.querySelector("[data-submit]");
  var formError = form.querySelector("[data-form-error]");
  var successBox = document.querySelector("[data-success]");
  var successEmail = document.querySelector("[data-success-email]");

  // ── Date helpers (user's local calendar day) ─────────────────────────────
  function localToday() {
    var d = new Date();
    return d.getFullYear() + "-" + pad(d.getMonth() + 1) + "-" + pad(d.getDate());
  }
  function pad(n) { return String(n).padStart(2, "0"); }
  function addDays(dateStr, n) {
    var d = new Date(dateStr + "T00:00:00Z");
    d.setUTCDate(d.getUTCDate() + n);
    return d.toISOString().slice(0, 10);
  }

  var today = localToday();
  els.window_opens_at.value = today;
  els.window_opens_at.min = today;
  els.window_closes_at.value = addDays(today, 14);
  syncCloseBounds();

  els.window_opens_at.addEventListener("change", function () {
    var v = els.window_opens_at.value;
    if (v) {
      var minClose = addDays(v, 1);
      var maxClose = addDays(v, MAX_WINDOW_DAYS);
      if (!els.window_closes_at.value || els.window_closes_at.value < minClose || els.window_closes_at.value > maxClose) {
        els.window_closes_at.value = addDays(v, 14);
      }
    }
    syncCloseBounds();
  });

  function syncCloseBounds() {
    var v = els.window_opens_at.value;
    els.window_closes_at.min = v ? addDays(v, 1) : today;
    els.window_closes_at.max = v ? addDays(v, MAX_WINDOW_DAYS) : "";
  }

  // ── Markets ───────────────────────────────────────────────────────────────
  function currentMarket() {
    var h = els.market_handle.value;
    for (var i = 0; i < markets.length; i++) if (markets[i].market_handle === h) return markets[i];
    return markets[0];
  }

  // Match the store's money format (bare "$" — see ABOUT_COMMUNITY_ORDERS.md
  // §6.1) used by the server-rendered tier ladder, so JS-updated amounts (after
  // a market change) read consistently with it.
  function fmtAmount(cents) {
    return "$" + Math.round(cents / 100);
  }

  function tierForDevices(n, percents) {
    if (!Number.isInteger(n) || n < MIN_DEVICES) return null;
    if (n <= 49) return { tier: "tier_0", percent: percents.tier_0 };
    if (n <= 99) return { tier: "tier_1", percent: percents.tier_1 };
    if (n <= 299) return { tier: "tier_2", percent: percents.tier_2 };
    return { tier: "tier_3", percent: percents.tier_3 };
  }

  // The tier ladder is rendered SERVER-SIDE for the active market (so it's
  // correct on load with no JS / failed fetch). We only repaint it if the user
  // actively changes the market picker.
  function renderMarketHint() {
    if (!marketHint) return;
    var m = currentMarket();
    marketHint.textContent =
      "Discount tiers in " + m.label + ": " +
      m.percents.tier_0 + "% off for 25–49 devices (about " + fmtAmount(m.tier_off_cents.tier_0) + " each), " +
      m.percents.tier_1 + "% for 50–99 (" + fmtAmount(m.tier_off_cents.tier_1) + "), " +
      m.percents.tier_2 + "% for 100–299 (" + fmtAmount(m.tier_off_cents.tier_2) + "), " +
      m.percents.tier_3 + "% for 300+ (" + fmtAmount(m.tier_off_cents.tier_3) + ").";
  }

  function renderDevicesHint() {
    if (!devicesHint) return;
    var m = currentMarket();
    var n = parseDevices(els.target_devices.value);
    var tier = tierForDevices(n, m.percents);
    devicesHint.textContent = tier
      ? tier.percent + "% off each device — about " + fmtAmount(m.tier_off_cents[tier.tier]) + " off per device"
      : "Minimum " + MIN_DEVICES + " devices.";
  }

  function rebuildMarketOptions() {
    var prev = els.market_handle.value || ACTIVE_MARKET;
    els.market_handle.innerHTML = "";
    markets.forEach(function (m) {
      var opt = document.createElement("option");
      opt.value = m.market_handle;
      opt.textContent = m.label;
      els.market_handle.appendChild(opt);
    });
    // Default to the active store market; respect a user's explicit choice.
    var pick = marketTouched ? prev : ACTIVE_MARKET;
    if (!hasMarket(pick)) pick = hasMarket(prev) ? prev : (markets[0] ? markets[0].market_handle : "US");
    els.market_handle.value = pick;
  }
  function hasMarket(h) {
    return markets.some(function (m) { return m.market_handle === h; });
  }

  els.market_handle.addEventListener("change", function () {
    marketTouched = true;
    renderMarketHint();
    renderDevicesHint();
  });
  els.target_devices.addEventListener("input", renderDevicesHint);
  // Market ladder is server-rendered for the active market; only the devices
  // hint needs an initial paint. (renderMarketHint runs if the user switches.)
  renderDevicesHint();

  fetch(WORKER_BASE + "/api/markets", { headers: { Accept: "application/json" } })
    .then(function (r) { return r.ok ? r.json() : null; })
    .then(function (data) {
      if (!data || !Array.isArray(data.markets) || data.markets.length === 0) return;
      markets = data.markets;
      rebuildMarketOptions();
      // Refresh the devices hint with live amounts; leave the server-rendered
      // ladder untouched unless the user changes the market.
      renderDevicesHint();
    })
    .catch(function () { /* keep fallback markets — the form must always work */ });

  // ── Turnstile ───────────────────────────────────────────────────────────
  var turnstileToken = null;
  var turnstileWidgetId = null;
  var turnstileEl = form.querySelector("[data-turnstile]");

  function renderTurnstile() {
    if (!turnstileEl || !TURNSTILE_SITEKEY) return;
    if (!window.turnstile) { setTimeout(renderTurnstile, 150); return; }
    if (turnstileWidgetId !== null) return;
    turnstileWidgetId = window.turnstile.render(turnstileEl, {
      sitekey: TURNSTILE_SITEKEY,
      theme: "auto",
      callback: function (t) { turnstileToken = t; clearError("turnstile"); },
      "error-callback": function () { turnstileToken = null; },
      "expired-callback": function () { turnstileToken = null; }
    });
  }
  function resetTurnstile() {
    turnstileToken = null;
    if (turnstileWidgetId !== null && window.turnstile) {
      try { window.turnstile.reset(turnstileWidgetId); } catch (e) { /* noop */ }
    }
  }
  renderTurnstile();

  // ── Validation (UX only; worker is authoritative) ─────────────────────────
  function parseDevices(s) {
    if (typeof s !== "string" || s.trim() === "") return NaN;
    var n = Number(s);
    return Number.isInteger(n) ? n : NaN;
  }

  function val(el) { return el && el.value ? el.value.trim() : ""; }

  function validate() {
    var errs = [];
    if (!val(els.display_name)) errs.push(["display_name", "Please enter a community or page name."]);
    if (!EMAIL_RE.test(val(els.organizer_email))) errs.push(["organizer_email", "Please enter a valid email address."]);
    if (val(els.organizer_phone).length > 40) errs.push(["organizer_phone", "Please enter a shorter phone number."]);
    var ot = val(els.org_type);
    if (ot && ORG_TYPES.indexOf(ot) === -1) errs.push(["org_type", "Please choose a valid organization type."]);
    var mh = els.market_handle.value;
    if (!mh || MARKET_HANDLES.indexOf(mh) === -1) errs.push(["market_handle", "Please choose a valid community location."]);
    if (!val(els.community_city_state)) errs.push(["community_city_state", "Please enter your city and state/province."]);
    var devices = parseDevices(els.target_devices.value);
    if (!Number.isInteger(devices)) errs.push(["target_devices", "Please enter the number of devices."]);
    else if (devices < MIN_DEVICES) errs.push(["target_devices", "The community program requires a minimum of " + MIN_DEVICES + " devices."]);
    var opens = els.window_opens_at.value;
    var closes = els.window_closes_at.value;
    if (!opens) errs.push(["window_opens_at", "Please choose a start date."]);
    if (!closes) errs.push(["window_closes_at", "Please choose an end date."]);
    if (opens && closes) {
      if (opens < today) errs.push(["window_opens_at", "Window open date can't be in the past."]);
      if (closes <= opens) errs.push(["window_closes_at", "Window close date must be after the open date."]);
      else if (closes > addDays(opens, MAX_WINDOW_DAYS)) errs.push(["window_closes_at", "The order window can't be longer than " + MAX_WINDOW_DAYS + " days."]);
    }
    return errs;
  }

  function clearError(field) {
    var el = form.querySelector('[data-error-for="' + field + '"]');
    if (el) el.textContent = "";
  }
  function clearAllErrors() {
    var nodes = form.querySelectorAll("[data-error-for]");
    for (var i = 0; i < nodes.length; i++) nodes[i].textContent = "";
    if (formError) { formError.hidden = true; formError.textContent = ""; }
  }
  function showErrors(pairs) {
    var first = null;
    pairs.forEach(function (p) {
      var el = form.querySelector('[data-error-for="' + p[0] + '"]');
      if (el && !el.textContent) { el.textContent = p[1]; if (!first) first = el; }
    });
    if (first && first.scrollIntoView) first.scrollIntoView({ behavior: "smooth", block: "center" });
  }
  function showFormError(msg) {
    if (!formError) return;
    formError.textContent = msg;
    formError.hidden = false;
  }

  // ── Submit ────────────────────────────────────────────────────────────────
  var FATAL = "Something went wrong creating your community page. Please contact lauren@tincan.kids for help.";

  form.addEventListener("submit", function (event) {
    event.preventDefault();
    if (submitBtn.disabled) return;
    clearAllErrors();

    var errs = validate();
    if (!turnstileToken) errs.push(["turnstile", "Please complete the verification above."]);
    if (errs.length > 0) { showErrors(errs); return; }

    var email = val(els.organizer_email);
    var payload = {
      display_name: val(els.display_name),
      organizer_name: val(els.organizer_name) || undefined,
      organizer_email: email,
      organizer_phone: val(els.organizer_phone) || undefined,
      org_type: val(els.org_type) || undefined,
      market_handle: els.market_handle.value,
      community_city_state: val(els.community_city_state),
      target_devices: parseDevices(els.target_devices.value),
      window_opens_at: els.window_opens_at.value,
      window_closes_at: els.window_closes_at.value,
      community_blurb: val(els.community_blurb) || undefined,
      marketing_consent: true,
      turnstileToken: turnstileToken
    };

    var fd = new FormData();
    fd.append("payload", JSON.stringify(payload));

    submitBtn.disabled = true;
    submitBtn.textContent = "Submitting…";

    fetch(WORKER_BASE + "/api/submit", { method: "POST", body: fd })
      .then(function (r) { return r.json(); })
      .then(function (res) {
        if (res && res.status === "ok") { showSuccess(res.organizerEmail || email); return; }
        if (res && res.status === "invalid") {
          showErrors((res.errors || []).map(function (e) { return [e.field, e.message]; }));
          resetTurnstile();
          restoreButton();
          return;
        }
        showFormError((res && res.message) || FATAL);
        resetTurnstile();
        restoreButton();
      })
      .catch(function () {
        showFormError(FATAL);
        resetTurnstile();
        restoreButton();
      });
  });

  function restoreButton() {
    submitBtn.disabled = false;
    submitBtn.textContent = "Submit";
  }

  function showSuccess(email) {
    if (successEmail) successEmail.textContent = email || "";
    form.hidden = true;
    form.style.display = "none";
    if (successBox) {
      successBox.hidden = false;
      if (successBox.scrollIntoView) successBox.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }
})();
